import { useState, useRef, useEffect } from 'react'
import { Avatar } from '../ui/Avatar'
import { useChatStore } from '../../stores/chatStore'
import { useAuthStore } from '../../stores/authStore'
import { chatsApi } from '../../api/chats'
import { useUiStore } from '../../stores/uiStore'
import { useFoldersStore } from '../../stores/foldersStore'
import { MuteDialog } from '../ui/MuteDialog'
import { chatTitle } from '../../lib/displayName'
import type { Chat } from '../../types'

interface ChatItemProps {
  chat: Chat
  isActive: boolean
  onClick: () => void
  onFolderChange?: () => void
}

interface MenuPos {
  x: number
  y: number
}


function formatTime(iso: string | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86_400_000)
  const weekAgo = new Date(today.getTime() - 6 * 86_400_000)
  if (d >= today) return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  if (d >= yesterday) return 'Вчера'
  if (d >= weekAgo) return d.toLocaleDateString('ru-RU', { weekday: 'short' })
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
}

export function ChatItem({ chat, isActive, onClick, onFolderChange }: ChatItemProps) {
  const unread = useChatStore((s) => s.unread[chat.id] ?? chat.unread_count)
  const typingSet = useChatStore((s) => s.typingUsers[chat.id])
  const { userId } = useAuthStore()
  const { addToast } = useUiStore()
  const { folders } = useFoldersStore()
  const title = chatTitle(chat)
  const avatarUrl = chat.avatar_url ?? chat.peer?.avatar_url
  const rawLast = chat.last_message?.text ?? ''
  const lastText = rawLast.startsWith('[[call]]') ? '📞 Звонок' : rawLast
  const someoneTyping = Array.from(typingSet ?? []).some((id) => id !== userId)

  const [menuPos, setMenuPos] = useState<MenuPos | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [showMuteDialog, setShowMuteDialog] = useState(false)
  const [draft, setDraft] = useState(() => localStorage.getItem(`ggram-draft-${chat.id}`) ?? '')

  useEffect(() => {
    if (isActive) return
    const id = setInterval(() => {
      setDraft(localStorage.getItem(`ggram-draft-${chat.id}`) ?? '')
    }, 2000)
    return () => clearInterval(id)
  }, [chat.id, isActive])

  useEffect(() => {
    if (!menuPos) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuPos(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuPos])

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    setMenuPos({ x: e.clientX, y: e.clientY })
  }

  const handlePin = async () => {
    setMenuPos(null)
    try {
      await chatsApi.updateSettings(chat.id, { is_pinned: !chat.is_pinned })
      useChatStore.getState().upsertChat({ ...chat, is_pinned: !chat.is_pinned })
    } catch {
      addToast('error', 'Не удалось обновить чат')
    }
  }

  const handleMuteClick = () => {
    if (chat.is_muted) {
      // unmute immediately
      setMenuPos(null)
      chatsApi.updateSettings(chat.id, { is_muted: false, mute_until: null })
        .then(() => useChatStore.getState().upsertChat({ ...chat, is_muted: false, mute_until: null }))
        .catch(() => addToast('error', 'Не удалось обновить чат'))
    } else {
      setMenuPos(null)
      setShowMuteDialog(true)
    }
  }

  const handleMuteConfirm = async (muteUntil: string | null) => {
    setShowMuteDialog(false)
    try {
      await chatsApi.updateSettings(chat.id, { is_muted: true, mute_until: muteUntil })
      useChatStore.getState().upsertChat({ ...chat, is_muted: true, mute_until: muteUntil })
    } catch {
      addToast('error', 'Не удалось отключить звук')
    }
  }

  const handleArchive = async () => {
    setMenuPos(null)
    const newFolder = chat.folder === 'archive' ? 'inbox' : 'archive'
    try {
      await chatsApi.updateSettings(chat.id, { folder: newFolder })
      useChatStore.getState().upsertChat({ ...chat, folder: newFolder })
      onFolderChange?.()
    } catch {
      addToast('error', 'Не удалось обновить чат')
    }
  }

  const handleMoveToFolder = async (folderId: string) => {
    setMenuPos(null)
    try {
      await chatsApi.updateSettings(chat.id, { folder: folderId })
      useChatStore.getState().upsertChat({ ...chat, folder: folderId })
      onFolderChange?.()
    } catch {
      addToast('error', 'Не удалось переместить чат')
    }
  }

  const handleMarkUnread = () => {
    setMenuPos(null)
    useChatStore.getState().incrementUnread(chat.id)
  }

  const isArchived = chat.folder === 'archive'

  return (
    <>
      <button
        onClick={onClick}
        onContextMenu={handleContextMenu}
        className={`
          w-full flex items-center gap-3 px-4 py-2 transition-colors text-left
          ${isActive ? 'bg-bg-active' : 'hover:bg-bg-hover'}
        `}
      >
        <Avatar name={title} src={avatarUrl} size={52} online={chat.peer?.online} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 min-w-0">
              <span className="text-txt-primary font-semibold truncate text-[15px]">{title}</span>
              {chat.is_muted && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" className="text-txt-meta shrink-0">
                  <path d="M16.5 12A4.5 4.5 0 0 0 14 7.97V10.18L16.45 12.65C16.5 12.44 16.5 12.22 16.5 12M19 12C19 12.94 18.8 13.82 18.46 14.64L19.97 16.15C20.63 14.91 21 13.5 21 12C21 7.72 18.01 4.14 14 3.23V5.29C16.89 6.15 19 8.83 19 12M4.27 3L3 4.27L7.73 9H3V15H7L12 20V13.27L16.25 17.52C15.58 18.04 14.83 18.45 14 18.7V20.77C15.38 20.45 16.63 19.82 17.68 18.96L19.73 21L21 19.73L12 10.73L4.27 3M12 4L9.91 6.09L12 8.18V4Z"/>
                </svg>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0 ml-1">
              {chat.is_pinned && !unread && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="text-txt-meta">
                  <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
                </svg>
              )}
              <span className="text-txt-meta text-[12px]">
                {formatTime(chat.last_message?.created_at)}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between mt-0.5">
            {someoneTyping ? (
              <span className="flex items-center gap-1 text-accent text-[13px]">
                <span className="flex items-center gap-0.5">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="inline-block w-1 h-1 rounded-full bg-accent animate-typing-dot"
                      style={{ animationDelay: `${i * 0.2}s` }}
                    />
                  ))}
                </span>
                <span>печатает...</span>
              </span>
            ) : !isActive && draft ? (
              <span className="text-[13px] truncate">
                <span className="text-red-400 font-medium">Черновик: </span>
                <span className="text-txt-secondary">{draft}</span>
              </span>
            ) : (
              <span className="text-txt-secondary text-[13px] truncate">{lastText}</span>
            )}
            {unread > 0 ? (
              <span className={`ml-2 shrink-0 text-white text-[11px] font-semibold
                               rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center
                               ${chat.is_muted ? 'bg-[#6d7883]' : 'bg-accent'}`}>
                {unread > 999 ? '999+' : unread}
              </span>
            ) : chat.is_pinned ? (
              <span className="ml-2 shrink-0 w-5 h-5" />
            ) : null}
          </div>
        </div>
      </button>

      {menuPos && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenuPos(null)} />
          <div
            ref={menuRef}
            className="fixed z-50 bg-bg-input rounded-xl shadow-xl py-1 min-w-[180px] animate-fade-in"
            style={{ left: menuPos.x, top: menuPos.y }}
          >
            {[
              {
                label: chat.is_pinned ? 'Открепить' : 'Закрепить',
                action: handlePin,
              },
              {
                label: chat.is_muted ? 'Включить звук' : 'Отключить звук',
                action: handleMuteClick,
              },
              {
                label: isArchived ? 'Разархивировать' : 'Архивировать',
                action: handleArchive,
              },
              ...(unread === 0 ? [{
                label: 'Отметить непрочитанным',
                action: handleMarkUnread,
              }] : []),
              ...folders
                .filter((f) => f.id !== 'archive' && f.id !== chat.folder)
                .map((f) => ({
                  label: `Переместить в ${f.name}`,
                  action: () => handleMoveToFolder(f.id),
                })),
            ].map((item) => (
              <button
                key={item.label}
                onClick={item.action}
                className="w-full text-left px-4 py-2 text-txt-primary text-sm hover:bg-bg-hover transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}
      {showMuteDialog && (
        <MuteDialog
          onConfirm={handleMuteConfirm}
          onCancel={() => setShowMuteDialog(false)}
        />
      )}
    </>
  )
}
